import { useEffect, useState } from 'react';
import { fetchHotels } from '../api/Hotels';
import { loadCityLocations } from '../api/Location';
import { Hotel, TripInfo } from '../types/Quotation';
import { getDatePlusDays } from '../utils/date';
import { getHotelMealType } from '../utils/hotelMealType';
import { QuotationRoomOccupancy, toHotelSearchRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';

type HotelCacheEntry = { data: Hotel[]; loading: boolean; error: any | null };

export const useHotelCache = (
  tripInfo: TripInfo,
  adultsOverride?: number,
  mealPlan?: string,
  hotelRooms?: QuotationRoomOccupancy[]
) => {
  const [hotelCache, setHotelCache] = useState<HotelCacheEntry[]>([]);
  const mealType = getHotelMealType(mealPlan);
  const roomsKey = JSON.stringify(Array.isArray(hotelRooms) ? hotelRooms : []);

  useEffect(() => {
    if (
      tripInfo.from?.value &&
      tripInfo.places.length > 0 &&
      tripInfo.startDate
    ) {
      const initial = tripInfo.places.map(() => ({ data: [], loading: true, error: null }));
      setHotelCache(initial);
      tripInfo.places.forEach((place, idx) => {
        const offset = tripInfo.places.slice(0, idx).reduce((s, p) => s + (p.nights || 0), 0);
        const ci = getDatePlusDays(tripInfo.startDate, offset);
        const co = getDatePlusDays(ci, place.nights || 0);
        (async () => {
          try {
            let cityCode = (place.value || '').trim();
            const isAirport = /^[A-Za-z]{3}$/.test(cityCode);
            const isCityId = /^\d+$/.test(cityCode);
            if (!isAirport && !isCityId) {
              const opts = await loadCityLocations(place.label || '');
              const match = (opts || []).find(o => o.type === 'city' && o.label?.toLowerCase().includes((place.label || '').toLowerCase()));
              if (match) cityCode = match.value;
            }
            const validCity = /^(?:[A-Za-z]{3}|\d+)$/.test(cityCode);
            const ciDate = new Date(ci);
            const coDate = new Date(co);
            if (!validCity || isNaN(ciDate.getTime()) || isNaN(coDate.getTime()) || ciDate >= coDate) {
              setHotelCache((prev) => {
                const next = [...prev];
                next[idx] = { data: [], loading: false, error: new Error('City or dates incomplete/invalid for hotel search') };
                return next;
              });
              return;
            }

            const rooms = Array.isArray(hotelRooms) && hotelRooms.length > 0
              ? toHotelSearchRooms(hotelRooms)
              : [{
                Adults: adultsOverride ?? tripInfo.travellers.adults,
                Children: tripInfo.travellers.children,
                ChildrenAges: [],
              }];

            const resp = await fetchHotels({
              city: cityCode,
              checkIn: ci,
              checkOut: co,
              rooms,
              filters: mealType ? { MealType: mealType } : undefined,
              page: 1,
            });
            const hotels: Hotel[] = (resp && (resp as any).items) || [];
            setHotelCache((prev) => {
              const next = [...prev];
              next[idx] = { data: hotels, loading: false, error: null };
              return next;
            });
          } catch (err: any) {
            setHotelCache((prev) => {
              const next = [...prev];
              next[idx] = { data: [], loading: false, error: err };
              return next;
            });
          }
        })();
      });
    }
  }, [tripInfo.from, tripInfo.places, tripInfo.startDate, tripInfo.travellers, adultsOverride, mealType, roomsKey, hotelRooms]);

  return { hotelCache };
};
